import React, { useState, useEffect, useRef } from 'react'
// import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
// import { dracula } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Highlight } from "prism-react-renderer"
import { themes } from "prism-react-renderer"
// import draculaTheme from "assets/themes/draculaTheme"

import { Editor } from "prism-react-editor"
import { BasicSetup } from "prism-react-editor/setups"
import PopupTooltip from "components/PopupTooltip"
import AIExplanation, { AIExplainIcon } from "components/AIExplanation"
import ChallengeTag from "components/ChallengeTag"

import "prism-react-editor/layout.css"
import "prism-react-editor/themes/dracula.css"

// Required by the basic setup
import "prism-react-editor/search.css"

import "prism-react-editor/prism/languages/python"
import "prism-react-editor/languages/python"

import * as validators from "utils/validators.js"
import * as lessonParser from "utils/lessonParser"
import { runPythonCode } from "utils/pythonRunner"


function TextBox({ questionData, questionVariables, questionText, questionStatus, onAnswerCheck, onHelpUsed, tag }) {

    const [hasEdited, setHasEdited] = useState(false);
    const aiRef = useRef();
    const lessonScrollRef = useRef(null);
    const popupRef = useRef(null);
    const inputTooltipRef = useRef(null);

    const editorRef = useRef(null);

    const [outputOpen, setOutputOpen] = useState(false)
    const [outputError, setOutputError] = useState("")
    const [outputProblems, setOutputProblems] = useState([])
    const [outputLogs, setOutputLogs] = useState([])
    const [isExecuting, setIsExecuting] = useState(false)
    const [waitingForInput, setWaitingForInput] = useState(false)
    const [inputPrompt, setInputPrompt] = useState("")
    const [userInput, setUserInput] = useState("")
    const inputResolveRef = useRef(null)
    const backgroundRunRef = useRef(null)

    const theme = themes.dracula;

    //     const codeExample = `total = 0

    // for num in range(15):
    //   print("number " + str(num))
    //   total += num`
    // const codeExample = `print("Hello World")`

    const defaultValue = lessonParser.replaceVariablesInText(questionData["data"]?.["default_code"] || "", questionVariables)
    const currentContent = useRef(defaultValue);

    const handleHelpClick = () => {
        // Hide the popup by marking it as seen
        if (popupRef.current) {
            popupRef.current.handleInteraction();
        }

        // Notify parent that help was used
        onHelpUsed?.();

        // Trigger AI explanation
        aiRef.current?.getAIExplanation(questionData, {
            question: questionText,
            code: defaultValue,
            finalCode: currentContent.current,
            logs: outputLogs,
            error: (typeof outputError === 'object' ? outputError.error || outputError.message || JSON.stringify(outputError) : outputError ?? "None"),
            issues: outputProblems
        });
        aiRef.current?.showSection();
        setTimeout(() => {
            if (lessonScrollRef.current) {
                lessonScrollRef.current.scrollTo({
                    top: lessonScrollRef.current.scrollHeight,
                    behavior: 'smooth'
                });
            }
        }, 200);
    };

    useEffect(() => {
        // Cleanup function to cancel any pending input resolves
        return () => {
            // If there's a pending input request, resolve it with empty string to clean up
            if (inputResolveRef.current) {
                inputResolveRef.current("");
                inputResolveRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        // console.log(inputSegments.current)
        onAnswerCheck(async (force) => {
            if ((!hasEdited || !currentContent.current) && !force) {
                return null;
            }

            // Prevent multiple simultaneous checks
            if (isExecuting) {
                return null;
            }

            // Cancel any pending input from previous background run
            if (inputResolveRef.current) {
                inputResolveRef.current("");
                inputResolveRef.current = null;
            }

            // Set executing state to make editor read-only
            setIsExecuting(true)
            setOutputOpen(true)
            setOutputLogs([]) // Clear previous logs

            // Custom input handler for async prompts
            const handleInput = async (promptText) => {
                return new Promise((resolve) => {
                    // The prompt was output as a string, remove it and add as prompt object for inline rendering
                    setOutputLogs(prev => {
                        const newLogs = [...prev];
                        // Only remove the last log if it exactly matches the prompt text
                        if (newLogs.length > 0 &&
                            typeof newLogs[newLogs.length - 1] === 'string' &&
                            newLogs[newLogs.length - 1] === promptText) {
                            newLogs.pop();
                        }
                        // Add it as a prompt object with the actual prompt text
                        newLogs.push({ type: 'prompt', text: promptText });
                        return newLogs;
                    });
                    setWaitingForInput(true)
                    setInputPrompt(promptText || "")
                    inputResolveRef.current = resolve
                });
            };

            // Callback to update logs in real-time as Python prints
            const handleOutputUpdate = (logLine) => {
                setOutputLogs(prev => [...prev, logLine]);
            };

            // Check if validators array contains ONLY valWithInputs
            const validators = questionData.data.val || [];
            const hasOnlyValWithInputs = validators.length > 0 &&
                validators.every(v => v.func === "valWithInputs");

            let pythonOutput;

            if (hasOnlyValWithInputs) {
                // Provide mock data for validation since valWithInputs doesn't use inputLogs/inputVars
                pythonOutput = {
                    success: true,
                    logs: [],
                    vars: {}
                };
            } else {
                // Wait for interactive run to complete
                pythonOutput = await runPythonCode(currentContent.current, handleInput, handleOutputUpdate)
            }

            // Clear executing state after code runs (or immediately for valWithInputs-only)
            setIsExecuting(false)
            setWaitingForInput(false)
            setInputPrompt("")
            setUserInput("")
            if (pythonOutput.success) {
                setOutputOpen(true)
                // Don't overwrite logs - they were already updated in real-time
                // setOutputLogs(pythonOutput.logs)
                setOutputError("") // Clear any previous compilation errors

                let parsedData = lessonParser.parseValueList(
                    questionData.data.val,
                    false,
                    questionVariables,
                    null,
                    { inputCode: currentContent.current, inputLogs: pythonOutput.logs.join("\n"), inputVars: pythonOutput.vars }
                );

                // Handle async validators (e.g., valWithInputs)
                const resolvedData = await Promise.all(
                    parsedData.map(async (data) => ({
                        ...data,
                        value: data.value instanceof Promise ? await data.value : data.value
                    }))
                );

                console.log("Parsed Data:", resolvedData)
                let allValid = resolvedData.every((data) => data.value.success);

                if (!allValid) {
                    // Collect all error messages from parsedData where value.success is false
                    const problems = resolvedData
                        .filter((data) => !data.value.success)
                        .map((data) => data.value.error)
                        .filter(Boolean);
                    console.log("Validation Problems:", problems)
                    setOutputProblems(problems);

                    // Trigger AI explanation for incorrect answer
                    if (!force) {
                        requestAnimationFrame(() => {
                            aiRef.current?.getAIExplanation(questionData, {
                                question: questionText,
                                code: defaultValue,
                                finalCode: currentContent.current,
                                logs: pythonOutput.logs,
                                error: pythonOutput.error?.error || "None",
                                issues: problems
                            });
                        });
                    }
                } else {
                    setOutputProblems([]);
                }

                // If hasOnlyValWithInputs, start interactive run now (after validation completes)
                if (hasOnlyValWithInputs) {
                    runPythonCode(currentContent.current, handleInput, handleOutputUpdate)
                        .catch(err => console.log("Background interactive run error:", err));
                }

                setTimeout(() => {
                    if (lessonScrollRef.current) {
                        lessonScrollRef.current.scrollTo({
                            top: lessonScrollRef.current.scrollHeight,
                            behavior: 'smooth'
                        });
                    }
                }, 10);

                return { isCorrect: allValid };

            } else {
                if (pythonOutput.error) {
                    const errorLine = pythonOutput.error?.lineNumber;
                    if (errorLine) {
                        console.log(editorRef)
                        const errorLineElement = editorRef.current?.lines[errorLine];
                        if (errorLineElement) {
                            // console.log("ERROR ", errorLineElement)
                            errorLineElement.style.backgroundColor = 'rgba(200, 0, 10, 0.2)';

                        }
                    }
                    setOutputError(pythonOutput.error)
                    setOutputProblems([]) // Clear validation problems when there's a compilation error

                    // Trigger AI explanation for execution error
                    if (!force) {
                        requestAnimationFrame(() => {
                            aiRef.current?.getAIExplanation(questionData, {
                                question: questionText,
                                code: defaultValue,
                                finalCode: currentContent.current,
                                logs: pythonOutput.logs,
                                error: pythonOutput.error?.error || "None",
                                issues: []
                            });

                        });
                    }
                } else {
                    setOutputError("Unknown error.")
                    setOutputProblems([])
                }
                setOutputOpen(true)

                setTimeout(() => {
                    if (lessonScrollRef.current) {
                        lessonScrollRef.current.scrollTo({
                            top: lessonScrollRef.current.scrollHeight,
                            behavior: 'smooth'
                        });
                    }
                }, 10);

                return { isCorrect: false };
            }
        });
    }, [onAnswerCheck, hasEdited]);


    const handleUpdate = (value) => {
        const lines = value.split('\n');
        if (lines.length <= 20) {
            currentContent.current = value; // Update only if line count <= 20
        } else {
            editorRef.current.textarea.value = currentContent.current; // Revert to last valid state
            editorRef.current.update(); // Update only if line count <= 20
        }
    };

    const logContainerRef = useRef(null);

    useEffect(() => {
        if (logContainerRef.current) {
            logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
        }
    }, [outputLogs, waitingForInput]);

    return (
        <div ref={lessonScrollRef} className=' text-text flex justify-center w-full overflow-auto max-h-full pb-8 pt-12 break-small:pt-3'>
            <div className='flex items-center flex-col w-full break-small:w-min h-full'>
                <div className="flex justify-center w-full">
                    <div className='px-10 break-small:px-0 w-full text-center break-small:text-left'>
                        {tag && <div className='mb-3'><ChallengeTag tag={tag} /></div>}
                        <h1 className='font-bold text-[5vw] break-small:text-4xl mb-[4vh] break-big:mb-[6vh]'>{questionText}</h1>
                    </div>
                </div>
                <div className="min-w-fit w-fit pb-8">
                    <div
                        onKeyDown={(e) => {
                            if (e.key !== "Enter" && e.key !== "Control" && e.key !== "Meta" && e.key !== "Shift") {
                                setHasEdited(true);
                            }
                        }}
                        className='TextBox text-left text-xl w-[630px] break-small:w-[700px] break-big:w-[800px] bg-background-light rounded-xl border-[3px] p-4 flex flex-col gap-2 max-h-[450px] h-[50vh] transition-[border-color]' style={{ borderColor: questionStatus["guessed"] ? (questionStatus["answer"] ? "var(--accent)" : "var(--warning)") : "var(--border)" }}>
                        <Editor
                            language="python"
                            ref={editorRef}
                            value={defaultValue}
                            onUpdate={(value) => handleUpdate(value)}

                            readOnly={questionStatus["guessed"] || isExecuting}
                            style={{ backgroundColor: "var(--code-light)", borderRadius: "0.75rem", height: '100%', overflow: 'auto', flexShrink: "1" }} >
                            {editor => <BasicSetup editor={editor} />}
                        </Editor>
                        <div onMouseEnter={() => { setOutputOpen(true) }} onMouseLeave={() => { if (!questionStatus["guessed"] && !waitingForInput) { setOutputOpen(false) } }} className='text-white relative flex items-center rounded-xl border-2 h-full w-full p-4 bg-code-light border-code' style={{ height: outputOpen ? "190px" : "20px", transition: "height 250ms ease-in", overflow: waitingForInput ? 'visible' : 'hidden' }}>
                            {/* <div  style={{ paddingTop: closed ? "0" : "1rem", paddingBottom: closed ? "0" : "1rem", transition: "padding-top 250ms ease-in,padding-bottom 250ms ease-in" }}> */}
                            <div className='transition-opacity absolute' style={{ opacity: outputOpen ? "0" : "1" }}>
                                <pre>{outputError ? "Error!" : "Output"}</pre>
                            </div>

                            <div ref={logContainerRef} className='absolute py-3 transition-opacity font-mono h-full' style={{ opacity: outputOpen ? "1" : "0", width: "calc(100% - 15px)", overflow: waitingForInput ? 'visible' : 'auto' }}>
                                {outputError ? <pre>{typeof outputError === 'object' ? outputError.error || outputError.message || JSON.stringify(outputError) : outputError}</pre> : (
                                    <div style={{ overflow: waitingForInput ? 'visible' : 'auto', maxHeight: '100%' }}>
                                        {outputLogs.map((log, ind) => {
                                            // Handle different log types
                                            if (typeof log === 'object') {
                                                if (log.type === 'input') {
                                                    // Completed input: show prompt (white) + value (purple) inline on same line  
                                                    return (
                                                        <div key={ind} style={{ whiteSpace: 'pre-wrap' }}>
                                                            <span>{log.prompt}</span><span style={{ color: '#c084fc' }}>{log.value}</span>
                                                        </div>
                                                    );
                                                } else if (log.type === 'prompt') {
                                                    // Active prompt waiting for input - show inline input field
                                                    const isLastLog = ind === outputLogs.length - 1;
                                                    if (waitingForInput && isLastLog) {
                                                        return (
                                                            <div key={ind} className='relative'>
                                                                <span>{log.text}</span><input
                                                                    type="text"
                                                                    value={userInput}
                                                                    autoFocus
                                                                    placeholder="Type here..."
                                                                    onChange={(e) => setUserInput(e.target.value)}
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === 'Enter' && inputResolveRef.current) {
                                                                            e.preventDefault()
                                                                            e.stopPropagation()
                                                                            const value = userInput
                                                                            // Replace the prompt log with prompt + input (inline, with purple text)
                                                                            setOutputLogs(prev => {
                                                                                const newLogs = [...prev];
                                                                                newLogs[newLogs.length - 1] = { type: 'input', prompt: log.text, value: value };
                                                                                return newLogs;
                                                                            });
                                                                            inputResolveRef.current(value)
                                                                            inputResolveRef.current = null
                                                                            setUserInput("")
                                                                            setWaitingForInput(false)
                                                                            setInputPrompt("")
                                                                        }
                                                                    }}
                                                                    className='bg-transparent border-none outline-none font-mono caret-purple-400 placeholder-gray-500 placeholder-opacity-50'
                                                                    style={{ minWidth: '100px', color: '#c084fc' }}
                                                                />
                                                                <PopupTooltip
                                                                    ref={inputTooltipRef}
                                                                    text="Press Enter to submit"
                                                                    open={waitingForInput}
                                                                    storageKey="seenInputTooltip"
                                                                    autoMarkSeenAfter={7000}
                                                                    autoClose={true}
                                                                    top="calc(100% + 8px)"
                                                                    width="max-content"
                                                                />
                                                            </div>
                                                        );
                                                    }
                                                    // Prompt not active, shouldn't happen but just in case
                                                    return null;
                                                }
                                            }
                                            // Regular string log
                                            return <div key={ind + log} style={{ whiteSpace: 'pre-wrap' }}>{log}</div>
                                        })}

                                        {/* Fallback: if waiting for input but no prompt object found, show input field */}
                                        {waitingForInput && !outputLogs.some((log, ind) => typeof log === 'object' && log.type === 'prompt' && ind === outputLogs.length - 1) && (
                                            <div className='relative'>
                                                <span>{inputPrompt}</span><input
                                                    type="text"
                                                    value={userInput}
                                                    autoFocus
                                                    placeholder="Type here..."
                                                    onChange={(e) => setUserInput(e.target.value)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' && inputResolveRef.current) {
                                                            e.preventDefault()
                                                            e.stopPropagation()
                                                            const value = userInput
                                                            // Add the completed input to logs as inline prompt+value
                                                            setOutputLogs(prev => [...prev, { type: 'input', prompt: inputPrompt, value: value }]);
                                                            inputResolveRef.current(value)
                                                            inputResolveRef.current = null
                                                            setUserInput("")
                                                            setWaitingForInput(false)
                                                            setInputPrompt("")
                                                        }
                                                    }}
                                                    className='bg-transparent border-none outline-none font-mono caret-purple-400 placeholder-gray-500 placeholder-opacity-50'
                                                    style={{ minWidth: '100px', color: '#c084fc' }}
                                                />
                                                <PopupTooltip
                                                    ref={inputTooltipRef}
                                                    text="Press Enter to submit"
                                                    open={waitingForInput}
                                                    storageKey="seenInputTooltip"
                                                    autoMarkSeenAfter={7000}
                                                    autoClose={true}
                                                    top="calc(100% + 8px)"
                                                    width="max-content"
                                                />
                                            </div>
                                        )}

                                        {!outputLogs.length && !waitingForInput && <h1>No Logs</h1>}
                                    </div>
                                )}
                            </div>
                            {/* </div> */}
                        </div>
                    </div>
                </div>

                {(questionStatus["guessed"] && !questionStatus["answer"]) && <div className='max-w-[90vw] w-full break-small:w-[700px] break-big:w-[800px] min-h-40 rounded-xl bg-warning-darker border-warning border-[3px] flex p-6 px-6  flex-col gap-5 mb-8 ' style={{ animation: "incorrectSection 450ms ease" }}>
                    <div className='flex flex-col sm:flex-row items-center justify-between'>
                        <h1 className='text-text text-2xl font-bold text-left'>Problems:</h1>
                        <button
                            className='pr-1 text-primary text-lg font-bold text-right flex items-center gap-2 outline-none hover:underline relative'
                            onClick={handleHelpClick}
                        ><AIExplainIcon /> See how to get the answer
                            <PopupTooltip
                                ref={popupRef}
                                text="Stuck? Use free AI to help you understand it better!"
                                open={questionStatus["guessed"] && !questionStatus["answer"]}
                                storageKey="seenHelpPopup"
                                autoMarkSeenAfter={2000}
                            />
                        </button>
                    </div>
                    <div className='text-white relative flex flex-col text-left rounded-xl border-2 w-full font-mono p-4 bg-code-light border-code text-lg font-extrabold'>
                        {outputError ? (
                            <pre className='w-full text-wrap'>The code did not compile, probably due to a spelling error.</pre>
                        ) : outputProblems.length > 0 ? (
                            outputProblems.map((problem, ind) => {
                                // Split on backticks to find code blocks
                                const parts = problem.split(/(`[^`]+`)/g);
                                return (
                                    <div className='w-full text-wrap mb-2 last:mb-0' key={ind}>
                                        {parts.map((part, i) => {
                                            if (part.startsWith('`') && part.endsWith('`')) {
                                                // Remove backticks and render with syntax highlighting
                                                const code = part.slice(1, -1);
                                                return (
                                                    <Highlight key={i} code={code} language="python" theme={theme}>
                                                        {({ tokens, getLineProps, getTokenProps }) => (
                                                            <code className="bg-code min-w-max inline-block px-2 py-1 whitespace-pre rounded font-mono text-base font-semibold">
                                                                {tokens.map((line, lineIdx) => {
                                                                    const { key, ...lineProps } = getLineProps({ line });
                                                                    return (
                                                                        <div key={lineIdx} {...lineProps}>
                                                                            {line.map((token, tokenIdx) => {
                                                                                const { key: tokenKey, ...tokenProps } = getTokenProps({ token });
                                                                                return <span key={tokenIdx} {...tokenProps} />
                                                                            })}
                                                                        </div>
                                                                    )
                                                                })}
                                                            </code>
                                                        )}
                                                    </Highlight>
                                                );
                                            }
                                            return <span key={i}>{part}</span>;
                                        })}
                                    </div>
                                );
                            })
                        ) : (
                            <h1>No Problems</h1>
                        )}
                    </div>
                    <AIExplanation ref={aiRef} />
                </div>}
            </div>
        </div>
    )
}


export default TextBox; 